import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Space, Typography, Button, Form, Input, Select, DatePicker, InputNumber, Tooltip, Alert, Modal, Checkbox, Switch } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, ExclamationCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { validateCampaignDates, validateLineItemBudgets } from '../utils/campaignValidation';
import { usePermify } from '@permify/react-role';
import moment from 'moment';

const { Text } = Typography;
const { confirm } = Modal;

/**
 * CampaignHeader Component
 *
 * Displays and manages campaign-level configuration within a campaign order.
 * Each campaign has:
 * - Type (Display, Video, External) - required
 * - Name (required)
 * - Budget allocation (required)
 * - Optional date range override (defaults to order dates)
 * - Clone action (creates a duplicate campaign)
 * - Delete action (disabled if only 1 campaign exists)
 *
 * Props:
 * @param {number} campaignIndex - Index of the campaign in the campaigns array
 * @param {Object} form - Ant Design form instance
 * @param {number} orderBudget - Total order budget for validation
 * @param {Object} orderDates - { startDate, endDate } from order level
 * @param {Function} onClone - Callback when clone button clicked
 * @param {Function} onDelete - Callback when delete button clicked
 * @param {number} campaignCount - Total number of campaigns (for delete constraint)
 * @param {Array} campaigns - All campaigns for budget calculations
 * @param {boolean} isAdvancedMode - Whether advanced mode is enabled
 * @param {boolean} showOnlyDeliveryItems - Filter state to show only line items marked for delivery
 * @param {Function} onDeliveryFilterChange - Callback when delivery filter toggle changes
 */
const CampaignHeader = React.memo(({
  campaignIndex,
  form,
  orderBudget,
  orderDates,
  onClone,
  onDelete,
  campaignCount,
  campaigns = [],
  isAdvancedMode = false,
  showOnlyDeliveryItems = false,
  onDeliveryFilterChange,
}) => {
  // Get current campaign data from form
  const campaignType = Form.useWatch(['campaigns', campaignIndex, 'type'], form);
  const campaignName = Form.useWatch(['campaigns', campaignIndex, 'name'], form);
  const campaignBudget = Form.useWatch(['campaigns', campaignIndex, 'budget'], form) || 0;
  const campaignStartDate = Form.useWatch(['campaigns', campaignIndex, 'startDate'], form);
  const campaignEndDate = Form.useWatch(['campaigns', campaignIndex, 'endDate'], form);
  const lineItems = Form.useWatch(['campaigns', campaignIndex, 'lineItems'], form) || [];
  const hasMediaPlanStrategy = Form.useWatch(['campaigns', campaignIndex, 'hasMediaPlanStrategy'], form);

  // Get campaign order name for checkbox functionality
  const campaignOrderName = Form.useWatch('name', form);

  // Track previous campaign type to detect changes
  const [previousType, setPreviousType] = React.useState(campaignType);

  // State for "Same as campaign order name" checkbox
  const [useCampaignOrderName, setUseCampaignOrderName] = useState(false);

  // Permission checking for budget warning visibility
  const { isAuthorized } = usePermify();
  const [showBudgetWarning, setShowBudgetWarning] = useState(false);

  // Check if user is Ad Ops (only Ad Ops should see budget warning)
  useEffect(() => {
    const checkPermissions = async () => {
      const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      setShowBudgetWarning(hasAdOpsPermission);
    };

    checkPermissions();
  }, [isAuthorized]);

  // Auto-update campaign name when campaign order name changes (if checkbox is checked)
  useEffect(() => {
    if (useCampaignOrderName && campaignOrderName) {
      form.setFieldValue(['campaigns', campaignIndex, 'name'], campaignOrderName);
      // Trigger validation to clear any errors
      setTimeout(() => {
        form.validateFields([['campaigns', campaignIndex, 'name']]).catch(() => {
          // Ignore validation errors
        });
      }, 0);
    }
  }, [campaignOrderName, useCampaignOrderName, form, campaignIndex]);

  // Calculate validation states
  const dateValidation = useMemo(() => {
    if (!orderDates.startDate || !orderDates.endDate) {
      return { valid: true, error: null };
    }

    return validateCampaignDates(
      {
        startDate: campaignStartDate,
        endDate: campaignEndDate,
      },
      orderDates.startDate,
      orderDates.endDate
    );
  }, [campaignStartDate, campaignEndDate, orderDates]);

  const budgetValidation = useMemo(() => {
    // Check if at least one line item exists (regardless of delivery status)
    if (!lineItems || lineItems.length === 0) {
      return {
        valid: false,
        error: 'At least one line item is required',
        warning: null,
      };
    }

    // Only validate budgets for line items marked for delivery
    const deliveryLineItems = lineItems.filter(item => item.markedForDelivery !== false);

    // If no line items marked for delivery, budgets are valid (0 allocation is okay)
    if (deliveryLineItems.length === 0) {
      return {
        valid: true,
        error: null,
        warning: 'No line items marked for delivery',
        totalAllocated: 0,
        utilization: 0,
        unallocated: campaignBudget,
      };
    }

    return validateLineItemBudgets(deliveryLineItems, campaignBudget);
  }, [lineItems, campaignBudget]);

  // Calculate budget utilization
  const budgetUtilization = useMemo(() => {
    if (!campaignBudget || campaignBudget === 0) return 0;

    // Only include line items marked for delivery
    const deliveryLineItems = lineItems.filter(item => item.markedForDelivery !== false);
    const totalLineItemBudget = deliveryLineItems.reduce(
      (sum, item) => sum + (parseFloat(item.budgetTotal) || 0),
      0
    );

    return Math.round((totalLineItemBudget / campaignBudget) * 100);
  }, [lineItems, campaignBudget]);

  // Calculate remaining budget across all campaigns
  const remainingOrderBudget = useMemo(() => {
    const totalCampaignBudgets = campaigns.reduce(
      (sum, campaign) => sum + (parseFloat(campaign.budget) || 0),
      0
    );
    return orderBudget - totalCampaignBudgets;
  }, [campaigns, orderBudget]);

  const campaignTypeLabels = {
    1: 'Display',
    7: 'External',
    8: 'Video',
  };

  // Only allow deletion if:
  // 1. There are multiple campaigns (must have at least 1 campaign)
  // 2. AND it's not the first campaign (Campaign 1 at index 0 cannot be deleted)
  const canDelete = campaignCount > 1 && campaignIndex > 0;

  // Handle clone
  const handleCloneClick = () => {
    if (onClone) {
      onClone(campaignIndex);
    }
  };

  // Handle campaign type change with confirmation
  const handleCampaignTypeChange = (newType) => {
    // Check if there are any creatives in line items
    const hasCreatives = lineItems.some(lineItem =>
      lineItem.adGroup?.adGroupCreatives &&
      lineItem.adGroup.adGroupCreatives.length > 0
    );

    // If no creatives exist, just change the type
    if (!hasCreatives) {
      form.setFieldValue(['campaigns', campaignIndex, 'type'], newType);
      setPreviousType(newType);
      return;
    }

    // Show confirmation if creatives exist
    confirm({
      title: 'Change Campaign Type?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Changing the campaign type will remove all uploaded creatives from line items in this campaign.</p>
          <p><strong>This action cannot be undone.</strong></p>
          <p>Do you want to continue?</p>
        </div>
      ),
      okText: 'Yes, Change Type',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        // Clear all adGroup creatives from all line items in this campaign
        const updatedLineItems = lineItems.map(lineItem => ({
          ...lineItem,
          adGroup: lineItem.adGroup ? {
            ...lineItem.adGroup,
            adGroupCreatives: []
          } : undefined
        }));

        // Update the form with cleared creatives and new type
        form.setFieldValue(['campaigns', campaignIndex, 'lineItems'], updatedLineItems);
        form.setFieldValue(['campaigns', campaignIndex, 'type'], newType);
        setPreviousType(newType);
      },
      onCancel() {
        // Revert to previous type if cancelled
        form.setFieldValue(['campaigns', campaignIndex, 'type'], previousType);
      },
    });
  };

  // Handle delete with confirmation
  const handleDeleteClick = () => {
    confirm({
      title: 'Delete Campaign?',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete Campaign ${campaignIndex + 1}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        onDelete(campaignIndex);
      },
    });
  };

  // Handle "Same as campaign order name" checkbox change
  const handleCampaignOrderNameCheckbox = (e) => {
    const checked = e.target.checked;
    setUseCampaignOrderName(checked);

    if (checked && campaignOrderName) {
      // Copy campaign order name to campaign name
      form.setFieldValue(['campaigns', campaignIndex, 'name'], campaignOrderName);
      // Clear validation error by validating the field after setting the value
      setTimeout(() => {
        form.validateFields([['campaigns', campaignIndex, 'name']]).catch(() => {
          // Ignore validation errors
        });
      }, 0);
    } else if (!checked) {
      // Clear campaign name when unchecked
      form.setFieldValue(['campaigns', campaignIndex, 'name'], '');
    }
  };

  return (
    <Card
      style={{
        marginBottom: 16,
        border: '2px solid #e8e8e8',
        borderRadius: '8px',
      }}
      bodyStyle={{ padding: '16px' }}
    >
      <Row gutter={[16, 16]}>
        {/* Campaign Header Row */}
        <Col span={24}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Text strong style={{ fontSize: 16 }}>
                Campaign {campaignIndex + 1}
              </Text>
              {campaignType && (
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({campaignTypeLabels[campaignType]})
                </Text>
              )}
            </Space>
            <Space>
              {onClone && (
                <Tooltip title="Create a copy of this campaign">
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={handleCloneClick}
                  >
                    Clone Campaign
                  </Button>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Remove this campaign">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteClick}
                  >
                    Delete Campaign
                  </Button>
                </Tooltip>
              )}
            </Space>
          </Space>
        </Col>

        {/* Campaign Configuration Row 1: Type and Name */}
        <Col span={24}>
          <Row gutter={16}>
            {/* Campaign Type */}
            <Col span={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong>Campaign Type *</Text>
                <Form.Item
                  name={[campaignIndex, 'type']}
                  fieldKey={[campaignIndex, 'type']}
                  rules={[
                    { required: true, message: 'Campaign type is required' },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select type"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={handleCampaignTypeChange}
                    options={[
                      { label: 'Display', value: 1, key: 1 },
                      { label: 'External', value: 7, key: 7 },
                      { label: 'Video', value: 8, key: 8 },
                    ]}
                  />
                </Form.Item>
              </Space>
            </Col>

            {/* Campaign Name */}
            <Col span={18}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong>Campaign Name *</Text>
                <Form.Item
                  name={[campaignIndex, 'name']}
                  fieldKey={[campaignIndex, 'name']}
                  rules={[
                    { required: true, message: 'Campaign name is required' },
                    { max: 100, message: 'Campaign name must be 100 characters or less' },
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder={`Campaign ${campaignIndex + 1}`}
                    maxLength={100}
                    disabled={useCampaignOrderName}
                  />
                </Form.Item>
                <Checkbox
                  checked={useCampaignOrderName}
                  onChange={handleCampaignOrderNameCheckbox}
                  disabled={!campaignOrderName}
                >
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Same as campaign order name
                  </Text>
                </Checkbox>
              </Space>
            </Col>
          </Row>
        </Col>

        {/* Campaign Configuration Row 2: Budget and Dates */}
        <Col span={24}>
          <Row gutter={16}>
            {/* Campaign Budget */}
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>Budget *</Text>
                  <Tooltip title="Budget allocated to this campaign. Line items within this campaign must not exceed this amount.">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
                <Form.Item
                  name={[campaignIndex, 'budget']}
                  fieldKey={[campaignIndex, 'budget']}
                  rules={[
                    { required: true, message: 'Budget is required' },
                    {
                      validator: async (_, value) => {
                        if (value && value < 0) {
                          throw new Error('Budget must be positive');
                        }
                        if (value && remainingOrderBudget < 0 && campaigns[campaignIndex]?.budget !== value) {
                          throw new Error('Total campaign budgets exceed order budget');
                        }
                      },
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0.00"
                    prefix="$"
                    min={0}
                    precision={2}
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
                {campaignBudget > 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {budgetUtilization}% allocated to line items
                  </Text>
                )}
              </Space>
            </Col>

            {/* Campaign Start Date (Optional) */}
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>Start Date</Text>
                  <Tooltip title="Optional. Leave empty to inherit from order start date.">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
                <Form.Item
                  name={[campaignIndex, 'startDate']}
                  fieldKey={[campaignIndex, 'startDate']}
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Order start"
                    format="MM/DD/YYYY"
                    onChange={(date) => {
                      // Normalize to start of day in UTC
                      const normalizedDate = date ? date.utc().startOf('day') : null;
                      form.setFieldValue(['campaigns', campaignIndex, 'startDate'], normalizedDate);
                    }}
                    disabledDate={(current) => {
                      if (!orderDates.startDate || !orderDates.endDate) return false;
                      return current < moment(orderDates.startDate).startOf('day') ||
                             current > moment(orderDates.endDate).endOf('day');
                    }}
                  />
                </Form.Item>
              </Space>
            </Col>

            {/* Campaign End Date (Optional) */}
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>End Date</Text>
                  <Tooltip title="Optional. Leave empty to inherit from order end date.">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
                <Form.Item
                  name={[campaignIndex, 'endDate']}
                  fieldKey={[campaignIndex, 'endDate']}
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Order end"
                    format="MM/DD/YYYY"
                    onChange={(date) => {
                      // Normalize to end of day in UTC
                      const normalizedDate = date ? date.utc().endOf('day') : null;
                      form.setFieldValue(['campaigns', campaignIndex, 'endDate'], normalizedDate);
                    }}
                    disabledDate={(current) => {
                      if (!orderDates.startDate || !orderDates.endDate) return false;
                      const startDate = campaignStartDate || orderDates.startDate;
                      return current < moment(startDate).startOf('day') ||
                             current > moment(orderDates.endDate).endOf('day');
                    }}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Col>

        {/* Budget Warning - appears right after budget row - only for Ad Ops users */}
        {showBudgetWarning && budgetValidation.valid && budgetValidation.warning && (
          <Col span={24}>
            <Alert
              type="warning"
              message="Budget Warning"
              description={budgetValidation.warning}
              showIcon
              style={{ marginBottom: 0 }}
            />
          </Col>
        )}

        {/* Media Plan Strategy Toggle */}
        <Col span={24}>
          <Row gutter={16}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space align="center">
                  <Text strong>Has Media Plan Strategy</Text>
                  <Form.Item
                    name={[campaignIndex, 'hasMediaPlanStrategy']}
                    fieldKey={[campaignIndex, 'hasMediaPlanStrategy']}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Space>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Enable this to indicate that Ad Ops will handle the strategic media planning.
                  Line items become optional when enabled.
                </Text>
                {hasMediaPlanStrategy && (
                  <Alert
                    message="Media Plan Strategy Enabled"
                    description="Line items are now optional. You can add line items as guidance for Ad Ops, or leave them empty for Ad Ops to create."
                    type="info"
                    showIcon
                  />
                )}
              </Space>
            </Col>
          </Row>
        </Col>

        {/* DSP Configuration - Advanced Mode Only */}
        {isAdvancedMode && (
          <Col span={24}>
            <Row gutter={16} align="bottom">
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Demand-Side Platform</Text>
                    <Tooltip title="Select the DSP for programmatic launching of this campaign">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                  <Form.Item
                    name={[campaignIndex, 'demandSidePlatform']}
                    fieldKey={[campaignIndex, 'demandSidePlatform']}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select DSP"
                      allowClear
                      options={[
                        { value: 'NEXXEN', label: 'NEXXEN' },
                        { value: 'LIQUIDM', label: 'LIQUIDM' },
                        { value: 'JAMLOOP', label: 'JAMLOOP' },
                      ]}
                    />
                  </Form.Item>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Ad Provider ID</Text>
                    <Tooltip title="Unique identifier for the ad provider">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                  <Form.Item
                    name={[campaignIndex, 'adProviderId']}
                    fieldKey={[campaignIndex, 'adProviderId']}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Enter Ad Provider ID"
                    />
                  </Form.Item>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Maximum Bid</Text>
                    <Tooltip title="Maximum bid amount for the campaign">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                  <Form.Item
                    name={[campaignIndex, 'maxBid']}
                    fieldKey={[campaignIndex, 'maxBid']}
                    rules={[
                      {
                        type: 'number',
                        min: 0.01,
                        message: 'Maximum bid must be greater than zero',
                        transform: (value) => {
                          const num = parseFloat(value);
                          return isNaN(num) ? undefined : num;
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="4.00"
                      prefix="$"
                      min={0.01}
                      step={0.01}
                      precision={2}
                    />
                  </Form.Item>
                </Space>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Space align="center">
                  <Switch
                    checked={showOnlyDeliveryItems}
                    onChange={onDeliveryFilterChange}
                  />
                  <Text strong>Show Delivery Items Only</Text>
                  <Tooltip title="Filter to display only line items marked for delivery">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Col>
        )}

        {/* Validation Alerts */}
        {!dateValidation.valid && dateValidation.error && (
          <Col span={24}>
            <Alert
              type="error"
              message="Date Range Error"
              description={dateValidation.error}
              showIcon
              style={{ marginBottom: 0 }}
            />
          </Col>
        )}

        {!budgetValidation.valid && budgetValidation.error && (
          <Col span={24}>
            <Alert
              type="error"
              message="Budget Allocation Error"
              description={budgetValidation.error}
              showIcon
              style={{ marginBottom: 0 }}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
});

CampaignHeader.displayName = 'CampaignHeader';

export default CampaignHeader;
